import type { FC } from 'react';
import { Svg } from '../brand/primitives';
import { SportArtwork } from './SportArtwork';
import type { SportIconComponent, SportIconProps } from './types';

/** Every size uses the same Lift family; only the selected resource resolution changes. */
const make = (sport: string, name: string): SportIconComponent => {
  const Component: FC<SportIconProps> = ({ size = 32, onLoad }) => (
    <Svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <SportArtwork sport={sport} size={size} {...(onLoad ? { onLoad } : {})} />
    </Svg>
  );
  Component.displayName = name;
  return Component;
};

export const PadelIcon = make('padel', 'PadelIcon');
export const TennisIcon = make('tennis', 'TennisIcon');
export const SquashIcon = make('squash', 'SquashIcon');
export const BadmintonIcon = make('badminton', 'BadmintonIcon');
export const PickleballIcon = make('pickleball', 'PickleballIcon');
export const TableTennisIcon = make('table-tennis', 'TableTennisIcon');
export const Football5Icon = make('football-5', 'Football5Icon');
export const Football7Icon = make('football-7', 'Football7Icon');
export const Football11Icon = make('football-11', 'Football11Icon');
export const BasketballIcon = make('basketball', 'BasketballIcon');
export const VolleyballIcon = make('volleyball', 'VolleyballIcon');
export const HandballIcon = make('handball', 'HandballIcon');
export const RugbyIcon = make('rugby', 'RugbyIcon');
export const WaterPoloIcon = make('water-polo', 'WaterPoloIcon');
export const TaekwondoIcon = make('taekwondo', 'TaekwondoIcon');
export const KarateIcon = make('karate', 'KarateIcon');
export const JudoIcon = make('judo', 'JudoIcon');
export const BJJIcon = make('bjj', 'BJJIcon');
export const KickboxingIcon = make('kickboxing', 'KickboxingIcon');
export const MuayThaiIcon = make('muay-thai', 'MuayThaiIcon');
export const BoxingIcon = make('boxing', 'BoxingIcon');
export const MMAIcon = make('mma', 'MMAIcon');
export const SwimmingIcon = make('swimming', 'SwimmingIcon');
export const JetSkiIcon = make('jet-ski', 'JetSkiIcon');
export const KayakIcon = make('kayak', 'KayakIcon');
export const GymIcon = make('gym', 'GymIcon');
export const CrossfitIcon = make('crossfit', 'CrossfitIcon');
export const YogaIcon = make('yoga', 'YogaIcon');
export const PilatesIcon = make('pilates', 'PilatesIcon');
export const DanceIcon = make('dance', 'DanceIcon');
export const CyclingIcon = make('cycling', 'CyclingIcon');
export const RunningIcon = make('running', 'RunningIcon');
export const HikingIcon = make('hiking', 'HikingIcon');
export const ClimbingIcon = make('climbing', 'ClimbingIcon');
export const SkiingIcon = make('skiing', 'SkiingIcon');
export const BowlingIcon = make('bowling', 'BowlingIcon');
export const BilliardsIcon = make('billiards', 'BilliardsIcon');
export const GolfIcon = make('golf', 'GolfIcon');
export const EquestrianIcon = make('equestrian', 'EquestrianIcon');
export const ArcheryIcon = make('archery', 'ArcheryIcon');
export const ShootingIcon = make('shooting', 'ShootingIcon');
export const FutsalIcon = make('futsal', 'FutsalIcon');
export const MotocrossIcon = make('motocross', 'MotocrossIcon');
export const ParaglidingIcon = make('paragliding', 'ParaglidingIcon');
export const KartingIcon = make('karting', 'KartingIcon');
export const PaintballIcon = make('paintball', 'PaintballIcon');
export const AirsoftIcon = make('airsoft', 'AirsoftIcon');
export const ScubaDivingIcon = make('scuba-diving', 'ScubaDivingIcon');
export const SurfingIcon = make('surfing', 'SurfingIcon');
export const KitesurfingIcon = make('kitesurfing', 'KitesurfingIcon');
export const WindsurfingIcon = make('windsurfing', 'WindsurfingIcon');
export const WakeboardingIcon = make('wakeboarding', 'WakeboardingIcon');
export const RaftingIcon = make('rafting', 'RaftingIcon');
export const HorseRacingIcon = make('horse-racing', 'HorseRacingIcon');
export const GymnasticsIcon = make('gymnastics', 'GymnasticsIcon');
export const FencingIcon = make('fencing', 'FencingIcon');
export const IceSkatingIcon = make('ice-skating', 'IceSkatingIcon');
export const SkateboardingIcon = make('skateboarding', 'SkateboardingIcon');
export const ParkourIcon = make('parkour', 'ParkourIcon');
export const PetanqueIcon = make('petanque', 'PetanqueIcon');
export const DartsIcon = make('darts', 'DartsIcon');
export const ChessIcon = make('chess', 'ChessIcon');
export const ClayShootingIcon = make('clay-shooting', 'ClayShootingIcon');
export const SnowboardIcon = make('snowboard', 'SnowboardIcon');
export const SailingIcon = make('sailing', 'SailingIcon');
export const BackgammonIcon = make('backgammon', 'BackgammonIcon');
export const CardsIcon = make('cards', 'CardsIcon');

// ─── Registry (dynamic-name lookup) ───────────────────────────────────────

export const SPORT_REGISTRY = {
  padel: PadelIcon,
  tennis: TennisIcon,
  squash: SquashIcon,
  badminton: BadmintonIcon,
  pickleball: PickleballIcon,
  'table-tennis': TableTennisIcon,
  'football-5': Football5Icon,
  'football-7': Football7Icon,
  'football-11': Football11Icon,
  basketball: BasketballIcon,
  volleyball: VolleyballIcon,
  handball: HandballIcon,
  rugby: RugbyIcon,
  'water-polo': WaterPoloIcon,
  taekwondo: TaekwondoIcon,
  karate: KarateIcon,
  judo: JudoIcon,
  bjj: BJJIcon,
  kickboxing: KickboxingIcon,
  'muay-thai': MuayThaiIcon,
  boxing: BoxingIcon,
  mma: MMAIcon,
  swimming: SwimmingIcon,
  'jet-ski': JetSkiIcon,
  kayak: KayakIcon,
  gym: GymIcon,
  crossfit: CrossfitIcon,
  yoga: YogaIcon,
  pilates: PilatesIcon,
  dance: DanceIcon,
  cycling: CyclingIcon,
  running: RunningIcon,
  hiking: HikingIcon,
  climbing: ClimbingIcon,
  skiing: SkiingIcon,
  bowling: BowlingIcon,
  billiards: BilliardsIcon,
  golf: GolfIcon,
  equestrian: EquestrianIcon,
  archery: ArcheryIcon,
  shooting: ShootingIcon,
  futsal: FutsalIcon,
  motocross: MotocrossIcon,
  paragliding: ParaglidingIcon,
  karting: KartingIcon,
  paintball: PaintballIcon,
  airsoft: AirsoftIcon,
  'scuba-diving': ScubaDivingIcon,
  surfing: SurfingIcon,
  kitesurfing: KitesurfingIcon,
  windsurfing: WindsurfingIcon,
  wakeboarding: WakeboardingIcon,
  rafting: RaftingIcon,
  'horse-racing': HorseRacingIcon,
  gymnastics: GymnasticsIcon,
  fencing: FencingIcon,
  'ice-skating': IceSkatingIcon,
  skateboarding: SkateboardingIcon,
  parkour: ParkourIcon,
  petanque: PetanqueIcon,
  darts: DartsIcon,
  chess: ChessIcon,
  'clay-shooting': ClayShootingIcon,
  snowboard: SnowboardIcon,
  sailing: SailingIcon,
  backgammon: BackgammonIcon,
  cards: CardsIcon,
} as const satisfies Record<string, SportIconComponent>;

export type SportName = keyof typeof SPORT_REGISTRY;
export const SPORT_NAMES: readonly SportName[] = Object.keys(SPORT_REGISTRY) as SportName[];

export interface SportIconWrapperProps extends SportIconProps {
  readonly name: SportName;
}

export const SportIcon: FC<SportIconWrapperProps> = ({ name, ...rest }) => {
  const Component = SPORT_REGISTRY[name];
  if (!Component) return null;
  return <Component {...rest} />;
};

/** Codes whose display name isn't the title-cased code (acronyms, brand casing). */
const SPORT_DISPLAY_OVERRIDES: Readonly<Record<string, string>> = {
  bjj: 'BJJ',
  mma: 'MMA',
  crossfit: 'CrossFit',
  petanque: 'Pétanque',
};

/**
 * English display name for a sport code (`football-5` → "Football 5",
 * `bjj` → "BJJ"). Codes are identifiers and must never be shown raw.
 */
export function sportDisplayName(sport: string): string {
  const override = SPORT_DISPLAY_OVERRIDES[sport];
  if (override) return override;
  return sport
    .split('-')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');
}
